import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { clientsAPI, entitiesAPI, departmentsAPI } from '../services/api';

const GlobalContext = createContext();

export function GlobalContextProvider({ children }) {
  const { user, isDPO, isSuperAdmin, isClientAdmin, isEntityAdmin, isViewer } = useAuth();

  // State
  const [selectedClientId, setSelectedClientIdState] = useState(null);
  const [selectedEntityId, setSelectedEntityIdState] = useState(null);
  const [selectedDepartmentId, setSelectedDepartmentIdState] = useState(null);
  const [clients, setClients] = useState([]);
  const [entities, setEntities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  // Role-based locking flags
  const [isClientLocked, setIsClientLocked] = useState(false);
  const [isEntityLocked, setIsEntityLocked] = useState(false);

  // Load context from sessionStorage on mount
  useEffect(() => {
    const stored = sessionStorage.getItem('global_context');
    if (stored) {
      try {
        const { clientId, entityId, departmentId } = JSON.parse(stored);
        setSelectedClientIdState(clientId || null);
        setSelectedEntityIdState(entityId || null);
        setSelectedDepartmentIdState(departmentId || null);
      } catch (e) {
        console.warn('Failed to parse global_context from sessionStorage', e);
      }
    }
  }, []);

  // Fetch clients whenever user changes (covers initial load + post-login)
  useEffect(() => {
    if (!user) return;
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const res = await clientsAPI.getAll();
        setClients(res.data || []);
      } catch (e) {
        console.error('Failed to fetch clients for GlobalContext', e);
        setClients([]);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, [user]);

  // Apply role-based locking when user becomes available
  useEffect(() => {
    if (!user) return;

    if (isEntityAdmin) {
      // Entity Admin: both client and entity are implicit and locked
      setSelectedClientIdState(user.client_id || null);
      setSelectedEntityIdState(user.entity_id || null);
      setIsClientLocked(true);
      setIsEntityLocked(true);
    } else if (isClientAdmin) {
      // Client Admin: client is locked, entity is selectable
      setSelectedClientIdState(user.client_id || null);
      setIsClientLocked(true);
      setIsEntityLocked(false);
      // Reset entity selection when client is locked
      setSelectedEntityIdState(null);
    } else if (isViewer) {
      // Viewers: silently scope to their implicit context (no UI shown)
      setSelectedClientIdState(user.client_id || null);
      setSelectedEntityIdState(user.entity_id || null);
      setIsClientLocked(true);
      setIsEntityLocked(true);
    } else {
      // DPO / Super Admin: both fully unlocked
      setIsClientLocked(false);
      setIsEntityLocked(false);
    }
  }, [user, isEntityAdmin, isClientAdmin, isViewer]);

  // Fetch entities when selectedClientId or user changes
  useEffect(() => {
    if (!user) return;
    const fetchEntities = async () => {
      if (!selectedClientId) {
        // No client selected: fetch all entities
        setLoadingEntities(true);
        try {
          const res = await entitiesAPI.getAll();
          setEntities(res.data || []);
        } catch (e) {
          console.error('Failed to fetch all entities for GlobalContext', e);
          setEntities([]);
        } finally {
          setLoadingEntities(false);
        }
        return;
      }

      // Client selected: fetch entities for that client
      setLoadingEntities(true);
      try {
        const res = await entitiesAPI.getAll(selectedClientId);
        setEntities(res.data || []);
      } catch (e) {
        console.error(`Failed to fetch entities for client ${selectedClientId}`, e);
        setEntities([]);
      } finally {
        setLoadingEntities(false);
      }
    };

    fetchEntities();
  }, [selectedClientId, user]);

  // Fetch departments when selectedEntityId changes
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!selectedEntityId) {
        // No entity selected: clear departments
        setDepartments([]);
        setSelectedDepartmentIdState(null);
        return;
      }

      // Entity selected: fetch departments for that entity
      setLoadingDepartments(true);
      try {
        const res = await departmentsAPI.getByEntity(selectedEntityId);
        setDepartments(res.data || []);
        // Reset department selection when entity changes
        setSelectedDepartmentIdState(null);
      } catch (e) {
        console.error(`Failed to fetch departments for entity ${selectedEntityId}`, e);
        setDepartments([]);
        setSelectedDepartmentIdState(null);
      } finally {
        setLoadingDepartments(false);
      }
    };

    fetchDepartments();
  }, [selectedEntityId]);

  // Wrapper for setSelectedClientId that applies locking rules
  const setSelectedClientId = (clientId) => {
    if (isClientLocked) {
      // Client is locked, ignore attempts to change it
      return;
    }
    setSelectedClientIdState(clientId);
    // Reset entity when client changes
    setSelectedEntityIdState(null);
  };

  // Wrapper for setSelectedEntityId that applies locking rules
  const setSelectedEntityId = (entityId) => {
    if (isEntityLocked) {
      // Entity is locked, ignore attempts to change it
      return;
    }
    setSelectedEntityIdState(entityId);
  };

  // Wrapper for setSelectedDepartmentId
  const setSelectedDepartmentId = (departmentId) => {
    setSelectedDepartmentIdState(departmentId);
  };

  // Clear context (DPO/SA only)
  const clearContext = () => {
    if (isClientLocked) {
      // Don't allow clearing locked context
      return;
    }
    setSelectedClientIdState(null);
    setSelectedEntityIdState(null);
    setSelectedDepartmentIdState(null);
    setDepartments([]);
    sessionStorage.removeItem('global_context');
    // Reset entities to all when context is cleared
    const fetchAllEntities = async () => {
      setLoadingEntities(true);
      try {
        const res = await entitiesAPI.getAll();
        setEntities(res.data || []);
      } catch (e) {
        console.error('Failed to fetch all entities on clearContext', e);
      } finally {
        setLoadingEntities(false);
      }
    };
    fetchAllEntities();
  };

  // Computed values
  const selectedClient = clients.find(c => c.id === selectedClientId) || null;
  const selectedEntity = entities.find(e => e.id === selectedEntityId) || null;

  // Persist to sessionStorage whenever selection changes
  useEffect(() => {
    const context = { clientId: selectedClientId, entityId: selectedEntityId, departmentId: selectedDepartmentId };
    sessionStorage.setItem('global_context', JSON.stringify(context));
  }, [selectedClientId, selectedEntityId, selectedDepartmentId]);

  const value = {
    // State
    selectedClientId,
    selectedEntityId,
    selectedDepartmentId,
    selectedClient,
    selectedEntity,
    clients,
    entities,
    departments,
    loadingClients,
    loadingEntities,
    loadingDepartments,
    // Actions
    setSelectedClientId,
    setSelectedEntityId,
    setSelectedDepartmentId,
    clearContext,
    // Flags
    isClientLocked,
    isEntityLocked,
  };

  return (
    <GlobalContext.Provider value={value}>
      {children}
    </GlobalContext.Provider>
  );
}

export function useGlobalContext() {
  const ctx = useContext(GlobalContext);
  if (!ctx) {
    throw new Error('useGlobalContext must be used within GlobalContextProvider');
  }
  return ctx;
}
